/**
 * ASC 606 Phase 11g — Estimate Revision Panel (11e change-in-estimate).
 *
 * Per-POB:
 *   - Lifecycle timeline: PENDING / CONSUMED / CANCELLED revisions
 *   - "Revise Estimate" modal → POST /revise-estimate (stages delta;
 *     daily consumer drains into catch-up JE)
 *   - "Cancel" inline action on PENDING rows → POST /revise-estimate/cancel
 *
 * Backend:
 *   GET /contracts/{id}/pobs/{pob_id}/estimate-revisions
 *   POST /contracts/{id}/pobs/{pob_id}/revise-estimate
 *   POST /contracts/{id}/pobs/{pob_id}/revise-estimate/cancel
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../../constants';
import DataTable, { Column } from './DataTable';
import Modal from './Modal';
import { FormRow, InlineError, inputStyle, btnPrimary, btnSecondary } from './CostLedgerPanel';

const API = `${API_V1}/revenue`;

interface Revision {
  history_id: number;
  status: string;
  revision_type: string;
  prior_value: number | null;
  new_value: number;
  delta_amount: number;
  amount_recognized_at_stage: number;
  rationale: string;
  staged_by: number | null;
  staged_at: string | null;
  consumed_at: string | null;
  consumed_je_id: number | null;
  consumed_je_number: string | null;
  cancelled_at: string | null;
  cancelled_by: number | null;
  cancellation_note: string | null;
}

interface Props {
  contractId: number;
  pobId: number;
  contractStatus: string;
  onStateChanged?: () => void;
}

export default function EstimateRevisionPanel({ contractId, pobId, contractStatus, onStateChanged }: Props) {
  const [rows, setRows] = useState<Revision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState<Revision | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/pobs/${pobId}/estimate-revisions?limit=200`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.revisions || []);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [contractId, pobId]);

  useEffect(() => {
    load();
  }, [load]);

  const statusBadge = (s: string) => {
    const bg =
      s === 'PENDING'
        ? 'rgba(234,179,8,0.15)'
        : s === 'CONSUMED'
        ? 'rgba(34,197,94,0.15)'
        : 'rgba(100,116,139,0.15)';
    const color =
      s === 'PENDING' ? 'var(--warning)' : s === 'CONSUMED' ? 'var(--positive)' : 'var(--text-secondary)';
    return (
      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: bg, color }}>
        {s}
      </span>
    );
  };

  const cols: Column[] = [
    { key: 'staged_at', label: 'Staged', width: 140, render: (v: string | null) => (v ? new Date(v).toLocaleString() : '-') },
    { key: 'status', label: 'Status', render: (v: string) => statusBadge(v) },
    { key: 'revision_type', label: 'Type' },
    {
      key: 'prior_value',
      label: 'Prior',
      align: 'right',
      render: (v: number | null) => (v == null ? '-' : v.toString()),
    },
    {
      key: 'new_value',
      label: 'New',
      align: 'right',
      render: (v: number) => v.toString(),
    },
    {
      key: 'delta_amount',
      label: 'Δ Catch-up',
      align: 'right',
      render: (v: number) => (
        <span style={{ color: v < 0 ? 'var(--negative)' : 'var(--positive)', fontWeight: 600 }}>{formatAmount(v)}</span>
      ),
    },
    { key: 'consumed_je_number', label: 'JE #', render: (v: string | null) => v || '-' },
    { key: 'rationale', label: 'Rationale', render: (v: string) => <span style={{ fontSize: 11 }}>{v}</span> },
    {
      key: 'history_id',
      label: '',
      render: (_v: number, row: Revision) =>
        row.status === 'PENDING' ? (
          <button
            type="button"
            onClick={() => setCancelOpen(row)}
            style={{
              padding: '3px 10px',
              fontSize: 11,
              fontWeight: 600,
              background: 'var(--negative)',
              color: 'white',
              border: 'none',
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => setReviseOpen(true)}
          style={btnPrimary}
          disabled={(contractStatus || '').toUpperCase() === 'ACTIVE'}
          title={(contractStatus || '').toUpperCase() === 'ACTIVE' ? 'Post-approval — route through modification wizard' : ''}
        >
          Revise Estimate
        </button>
      </div>

      {(contractStatus || '').toUpperCase() === 'ACTIVE' && (
        <div
          style={{
            padding: 10,
            background: 'rgba(234,179,8,0.1)',
            border: '1px solid var(--warning)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--warning)',
          }}
        >
          Contract is ACTIVE. Estimate revisions are a judgment — post-approval changes must route through the modification wizard (Phase 7d).
        </div>
      )}

      {error && (
        <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)', borderRadius: 4, fontSize: 12, color: 'var(--negative)' }}>
          {error}
        </div>
      )}
      {flash && (
        <div style={{ padding: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid var(--positive)', borderRadius: 4, fontSize: 12, color: 'var(--positive)' }}>
          {flash}
        </div>
      )}

      <DataTable
        columns={cols}
        rows={rows}
        loading={loading}
        emptyMessage="No estimate revisions on this POB."
        rowKey="history_id"
      />

      <ReviseModal
        open={reviseOpen}
        onClose={() => setReviseOpen(false)}
        contractId={contractId}
        pobId={pobId}
        onSaved={async (msg) => {
          setReviseOpen(false);
          setFlash(msg);
          await load();
          if (onStateChanged) onStateChanged();
        }}
      />
      {cancelOpen && (
        <CancelModal
          revision={cancelOpen}
          onClose={() => setCancelOpen(null)}
          contractId={contractId}
          pobId={pobId}
          onSaved={async (msg) => {
            setCancelOpen(null);
            setFlash(msg);
            await load();
            if (onStateChanged) onStateChanged();
          }}
        />
      )}
    </div>
  );
}

function ReviseModal({
  open,
  onClose,
  contractId,
  pobId,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  contractId: number;
  pobId: number;
  onSaved: (flash: string) => void;
}) {
  const [revisionType, setRevisionType] = useState('COST_BASELINE');
  const [newValue, setNewValue] = useState('');
  const [rationale, setRationale] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(null);
    const v = parseFloat(newValue);
    if (!(v > 0)) {
      setErr('New value must be > 0.');
      return;
    }
    if (rationale.trim().length < 30) {
      setErr('Rationale must be at least 30 chars (SOX 404 audit trail).');
      return;
    }
    setBusy(true);
    try {
      const res = await authFetch(
        `${API}/contracts/${contractId}/pobs/${pobId}/revise-estimate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            revision_type: revisionType,
            new_value: v,
            rationale: rationale.trim(),
          }),
        },
      );
      if (res.ok) {
        const data = await res.json();
        onSaved(`Revision staged: Δ ${formatAmount(data.delta_amount)} (history #${data.history_id}). Daily consumer will drain.`);
        setNewValue('');
        setRationale('');
      } else {
        const body = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof body.detail === 'string' ? body.detail : body.detail?.message || JSON.stringify(body.detail);
        setErr(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Stage Estimate Revision"
      description="ASC 606-10-25-37 + ASC 250-10-45-17 — staged delta drains daily into cumulative catch-up JE."
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={submit} style={btnPrimary} disabled={busy}>
            {busy ? 'Staging...' : 'Stage Revision'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <FormRow label="Revision Type">
          <select value={revisionType} onChange={(e) => setRevisionType(e.target.value)} style={inputStyle}>
            <option value="COST_BASELINE">COST_BASELINE (estimated_total_cost)</option>
            <option value="TARGET_UNITS">TARGET_UNITS (target_total_units)</option>
          </select>
        </FormRow>
        <FormRow label="New Value">
          <input type="number" value={newValue} onChange={(e) => setNewValue(e.target.value)} style={inputStyle} step="0.01" />
        </FormRow>
        <FormRow label="Rationale (≥30 chars)">
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
            placeholder="Why is the estimate being revised? New vendor quote, scope change, measurement refinement..."
          />
          <div style={{ fontSize: 10, color: rationale.length >= 30 ? 'var(--positive)' : 'var(--text-hint)', marginTop: 2 }}>
            {rationale.length}/30
          </div>
        </FormRow>
        {err && <InlineError>{err}</InlineError>}
      </div>
    </Modal>
  );
}

function CancelModal({
  revision,
  onClose,
  contractId,
  pobId,
  onSaved,
}: {
  revision: Revision;
  onClose: () => void;
  contractId: number;
  pobId: number;
  onSaved: (flash: string) => void;
}) {
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      const res = await authFetch(
        `${API}/contracts/${contractId}/pobs/${pobId}/revise-estimate/cancel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note.trim() || null }),
        },
      );
      if (res.ok) {
        onSaved(`Revision #${revision.history_id} cancelled.`);
      } else {
        const body = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof body.detail === 'string' ? body.detail : body.detail?.message || JSON.stringify(body.detail);
        setErr(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Cancel Revision #${revision.history_id}`}
      description={`Status PENDING — no JE has been posted. Delta: ${formatAmount(revision.delta_amount)}`}
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={busy}>
            Keep
          </button>
          <button
            type="button"
            onClick={submit}
            style={{ ...btnPrimary, background: 'var(--negative)' }}
            disabled={busy}
          >
            {busy ? 'Cancelling...' : 'Cancel Revision'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <FormRow label="Cancellation Note (optional)">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
            placeholder="e.g. Superseded by new vendor quote..."
          />
        </FormRow>
        {err && <InlineError>{err}</InlineError>}
      </div>
    </Modal>
  );
}
